import { extractDataOrFailure, useDYFetch } from "./fetch"
import { useDYAppConfig } from "./config"
import type { EdgeCacheParams } from "./edge-cache"

// 2026-06-30 [api-composable-cache] 공개 화이트리스트 GET(getFromKey/getFromKeyArray) 기본 엣지 TTL.
//   사이트설정은 거의 안 바뀌는 데이터라 길게(12h). 호출자가 params.edgeCache 로 오버라이드 가능(그 값 우선).
//   백엔드는 편집 시 옵저버가 SiteSettingUtility:index 엣지태그를 자동 purge → TTL 길어도 stale 위험 낮음.
const EDGE_TTL = '12h' as const

// ============================================ MODEL ============================================
export default interface DYSiteSetting {
  no: number,
  categoryNo: number,
  settingValue: boolean,
  hangeulComment: boolean,  ////설정값 간단한 한글설명
  readOnlyValue: boolean,
  openAllAdmin: boolean
}

export interface DYSiteSettingShowParams extends EdgeCacheParams {
  domain? : string
}

// export interface DYSiteSettingStoreParams {
//   ///TOODO
// }

// type GetPaymentInfo = 'delivery_cost_yn'  | 'book_delivery_point' | 'shop_delivery_point' | 'book_delivery_cost'
//   | 'shop_delivery_cost' | 'book_delivery_cost_yn' | 'shop_delivery_cost_yn'

type SiteSetting<Type> = {
  [Key in keyof Type]: Type[Key]
}

export type GetPaymentInfoType = {
  delivery_cost_yn : 'y'|'n'
  book_delivery_cost_yn : 'y'|'n'
  shop_delivery_cost_yn : 'y'|'n'
  book_delivery_point : number
  shop_delivery_point : number
  book_delivery_cost : number
  shop_delivery_cost : number
}

type GetPaymentInfo = SiteSetting<GetPaymentInfoType>

export function useDYSiteSettingApi () {
  const { API_BASE_URL } = useDYAppConfig()

  const get = async (pk: number, params?: DYSiteSettingShowParams) => {
    // 2026-06-30 [api-composable-cache] show=민감 전체행 → 백엔드 엣지X(서버캐시만). edge 기본값 부여하지 않음.
    //   (호출자가 edgeCache 를 넘기지 않는 한 엣지에 안 올라감 — 현재 호출처 없음)
    const _params = {
      ...params
    }

    const response = await useDYFetch<DYSiteSetting>(`${API_BASE_URL}/api/site-setting-utilitys/${pk}`, { query: _params, immediate: false })
    await response.execute({ dedupe: 'defer' })
    return extractDataOrFailure(response)
  }

  const getByKey = async (settingKeyName: string, params?: DYSiteSettingShowParams) => {
    // 2026-06-30 [api-composable-cache] getFromKey=공개 화이트리스트 → 엣지 적격. 기본 TTL 부여(호출자 오버라이드 우선).
    const _params = {
      ...params,
      edgeCache: params?.edgeCache ?? EDGE_TTL,
    }
    const response = await useDYFetch<DYSiteSetting>(`${API_BASE_URL}/api/site-setting-utilitys/from-key/${settingKeyName}`, { query: _params, immediate: false })
    await response.execute({ dedupe: 'defer' })
    return extractDataOrFailure(response)
  }

  /**
   * 사이트 세팅 값들을 여러 개요청할 경우 사용됨. 리턴 가능한 키는 정해져있으니 해당 api컨트롤러에서 새로 지정 필요할 수 있음
   * 요청시 [요청키 => 밸류] 형태의 배열로 오게됨
   * @param settingKeyArray
   * @param params
   */

  async function getByKeyArray<T>(settingKeyArray: string[], params?: DYSiteSettingShowParams) {
    // 2026-06-30 [api-composable-cache] getFromKeyArray=공개 화이트리스트 → 엣지 적격. 기본 TTL 부여(호출자 오버라이드 우선).
    //   ⚠️ delivery_cost_yn 은 시간의존 무료배송 이벤트라 백엔드가 캐시 밖에서 매 요청 fresh override 한다.
    //      이 키가 섞이면 엣지에 박제될 경우 이벤트 시작/종료가 TTL까지 반영 안 되므로 엣지 자체를 제외한다.
    const _params = {
      ...params,
      edgeCache: settingKeyArray.includes('delivery_cost_yn')
        ? undefined
        : (params?.edgeCache ?? EDGE_TTL),
    }
    const response = await useDYFetch<T>(`${API_BASE_URL}/api/site-setting-utilitys/from-key-array/${settingKeyArray}`, {query: _params, immediate: false})
    await response.execute({ dedupe: 'defer' })
    return extractDataOrFailure(response)
  }

  return {
    get, getByKey, getByKeyArray
  }
}
